import { DnD5eCharacterService } from './character/DnD5eCharacterService.js'
import WorldOfDarknessCharacterService from './character/WorldOfDarknessCharacterService.js'
import { computed } from 'vue'
import { getGameVersionCode } from '@/config/gameVersions.js'

/**
 * Main Character Service - Factory for version-specific services
 */
class CharacterService {
  constructor() {
    // Default to the canonical version id for D&D 5e
    this.gameVersion = 'dnd_5e'
    this.services = {}

    // Register known version services under both the version id and the short code
    const d5Service = new DnD5eCharacterService()
    const d5Code = getGameVersionCode('dnd_5e') // typically '5e'
    this.registerService(d5Code, d5Service)
    this.registerService('dnd_5e', d5Service)
    // Register World of Darkness service under both id and short code (code from config is 'WoD')
    try {
      const wodService = WorldOfDarknessCharacterService
      const wodCode = getGameVersionCode('world_of_darkness')
      this.registerService(wodCode, wodService)
      this.registerService('world_of_darkness', wodService)
    } catch {
      // If registration fails for any reason, don't break initialization
    }
  }

  /**
   * Set the game version for API calls
   * @param {string} gameVersion
   */
  setGameVersion(gameVersion) {
    // Accept either a short code (e.g. '5e') or a version id (e.g. 'dnd_5e')
    this.gameVersion = gameVersion
    const svc = this.getCurrentService()
    if (svc && typeof svc.setGameVersion === 'function') {
      svc.setGameVersion(gameVersion)
    }
  }

  /**
   * Get the current version-specific service
   * @returns {BaseCharacterService} Version-specific service
   */
  getCurrentService() {
    // Try direct lookup first (exact key)
    if (this.services[this.gameVersion]) return this.services[this.gameVersion]

    // Next, try resolving short code for known version ids (e.g. 'dnd_5e' -> '5e')
    try {
      const code = getGameVersionCode(this.gameVersion)
      if (code && this.services[code]) return this.services[code]
    } catch {
      // ignore resolution errors and fall back to scanning keys
    }

    // Last resort: case-insensitive key match
    const keys = Object.keys(this.services)
    for (const k of keys) {
      if (String(k).toLowerCase() === String(this.gameVersion).toLowerCase())
        return this.services[k]
    }

    throw new Error(`No service found for game version: ${this.gameVersion}`)
  }

  /**
   * Register a new version-specific service
   * @param {string} gameVersion Game version identifier
   * @param {BaseCharacterService} service Service implementation
   */
  registerService(gameVersion, service) {
    this.services[gameVersion] = service
  }

  // Delegate all methods to the current service
  async isUserAdmin() {
    return this.getCurrentService().isUserAdmin()
  }

  async isUserDM() {
    return this.getCurrentService().isUserDM()
  }

  async canAccessCharacter(characterId) {
    return this.getCurrentService().canAccessCharacter(characterId)
  }

  getEmptyCharacter() {
    return this.getCurrentService().getEmptyCharacter()
  }

  async getCharacters(userId) {
    return this.getCurrentService().getCharacters(userId)
  }

  async getCharacter(characterId) {
    return this.getCurrentService().getCharacter(characterId)
  }

  async saveCharacter(character, schema = null) {
    // Pass optional pre-loaded schema object through to version service
    const service = this.getCurrentService()
    return service.saveCharacter
      ? await service.saveCharacter(character, schema)
      : await service.saveCharacter(character)
  }

  async updateCharacterFields(characterId, patch, opts = {}) {
    return this.getCurrentService().updateCharacterFields(characterId, patch, opts)
  }

  async deleteCharacter(characterId) {
    return this.getCurrentService().deleteCharacter(characterId)
  }

  async getSharedCharacters(userId) {
    return this.getCurrentService().getSharedCharacters(userId)
  }

  async shareCharacter(characterId, userIds) {
    return this.getCurrentService().shareCharacter(characterId, userIds)
  }

  async dataURLtoBlob(dataUrl) {
    return this.getCurrentService().dataURLtoBlob(dataUrl)
  }

  async checkConnectionStatus() {
    return this.getCurrentService().checkConnectionStatus()
  }

  async resetConnection() {
    return this.getCurrentService().resetConnection()
  }

  async getDMCharacters() {
    return this.getCurrentService().getDMCharacters()
  }

  async getAllCharacters() {
    return this.getCurrentService().getAllCharacters()
  }

  getCachedPublicCharacters(options = {}) {
    const service = this.getCurrentService()
    return typeof service.getCachedPublicCharacters === 'function'
      ? service.getCachedPublicCharacters(options)
      : null
  }

  clearPublicCharactersCache() {
    const service = this.getCurrentService()
    if (typeof service.clearPublicCharactersCache === 'function') {
      service.clearPublicCharactersCache()
    }
  }

  async getPublicCharacters(options = {}) {
    return this.getCurrentService().getPublicCharacters(options)
  }

  async getCharactersByCampaign(campaignId) {
    return this.getCurrentService().getCharactersByCampaign(campaignId)
  }

  async addCharacterToCampaign(characterId, campaignId) {
    return this.getCurrentService().addCharacterToCampaign(characterId, campaignId)
  }

  async removeCharacterFromCampaign(characterId, campaignId) {
    return this.getCurrentService().removeCharacterFromCampaign(characterId, campaignId)
  }

  getCharacterCardData(character) {
    return this.getCurrentService().getCharacterCardData(character)
  }

  setCharacterThumbnail(character, imageUrl) {
    return this.getCurrentService().setCharacterThumbnail(character, imageUrl)
  }

  async generateThumbnail(imageUrl) {
    return this.getCurrentService().generateThumbnail(imageUrl)
  }

  exportCharacter(character) {
    return this.getCurrentService().exportCharacter(character)
  }

  importCharacter(jsonData) {
    return this.getCurrentService().importCharacter(jsonData)
  }

  calculateProficiencyBonus(level) {
    return this.getCurrentService().calculateProficiencyBonus(level)
  }

  getAbilityModifier(score) {
    return this.getCurrentService().getAbilityModifier(score)
  }

  getAbilityModifierValue(score) {
    return this.getCurrentService().getAbilityModifierValue(score)
  }

  getMaxAbilityScore() {
    return this.getCurrentService().getMaxAbilityScore()
  }

  // D&D 5e specific methods (delegated to 5e service)
  getSkillModifier(character, skillName) {
    return this.getCurrentService().getSkillModifier(character, skillName)
  }

  getSavingThrowModifier(character, ability) {
    return this.getCurrentService().getSavingThrowModifier(character, ability)
  }

  getSpellSaveDC(character) {
    return this.getCurrentService().getSpellSaveDC(character)
  }

  getSpellAttackBonus(character) {
    return this.getCurrentService().getSpellAttackBonus(character)
  }

  getHitPointMaximum(character) {
    return this.getCurrentService().getHitPointMaximum(character)
  }

  getClassHitDie(className) {
    return this.getCurrentService().getClassHitDie(className)
  }

  // New delegators for class/subclass helpers
  async fetchClassDetails(className, forceRefresh = false) {
    return this.getCurrentService().fetchClassDetails
      ? await this.getCurrentService().fetchClassDetails(className, forceRefresh)
      : undefined
  }

  canonicalizeClassName(className, availableClasses) {
    return this.getCurrentService().canonicalizeClassName
      ? this.getCurrentService().canonicalizeClassName(className, availableClasses)
      : className
  }

  isCustomClass(className) {
    return this.getCurrentService().isCustomClass
      ? this.getCurrentService().isCustomClass(className)
      : false
  }

  getSubclassOptions(className) {
    return this.getCurrentService().getSubclassOptions
      ? this.getCurrentService().getSubclassOptions(className)
      : []
  }

  getArmorClass(character) {
    return this.getCurrentService().getArmorClass(character)
  }

  getInitiativeModifier(character) {
    return this.getCurrentService().getInitiativeModifier(character)
  }

  getSpellSlots(character) {
    return this.getCurrentService().getSpellSlots(character)
  }

  getClassFeatures(character) {
    return this.getCurrentService().getClassFeatures(character)
  }

  getRaceTraits(character) {
    return this.getCurrentService().getRaceTraits(character)
  }

  canCastSpells(character) {
    return this.getCurrentService().canCastSpells(character)
  }

  getCarryingCapacity(character) {
    return this.getCurrentService().getCarryingCapacity(character)
  }

  getPushDragLiftCapacity(character) {
    return this.getCurrentService().getPushDragLiftCapacity(character)
  }

  getPassivePerception(character) {
    return this.getCurrentService().getPassivePerception(character)
  }

  getPassiveInvestigation(character) {
    return this.getCurrentService().getPassiveInvestigation(character)
  }

  getPassiveInsight(character) {
    return this.getCurrentService().getPassiveInsight(character)
  }

  getPassiveScores(character) {
    return this.getCurrentService().getPassiveScores(character)
  }

  // Conditions and exhaustion helpers
  getConditionDescription(name) {
    return this.getCurrentService().getConditionDescription
      ? this.getCurrentService().getConditionDescription(name)
      : ''
  }

  getExhaustionPenalties(level) {
    return this.getCurrentService().getExhaustionPenalties
      ? this.getCurrentService().getExhaustionPenalties(level)
      : []
  }

  // XP / Progression helpers (delegated to version-specific services)
  getNextLevelXP(character) {
    return this.getCurrentService().getNextLevelXP(character)
  }

  getExperienceProgress(character) {
    return this.getCurrentService().getExperienceProgress(character)
  }

  // Detailed experience progress diagnostics (returns object with thresholds and computed progress)
  getExperienceProgressDetails(character) {
    return this.getCurrentService().getExperienceProgressDetails
      ? this.getCurrentService().getExperienceProgressDetails(character)
      : { progress: this.getExperienceProgress(character) }
  }

  getRemainingXPForNextLevel(character) {
    return this.getCurrentService().getRemainingXPForNextLevel
      ? this.getCurrentService().getRemainingXPForNextLevel(character)
      : 0
  }

  // Delegator to get threshold XP for a numeric level
  getThresholdForLevel(level) {
    return this.getCurrentService().getThresholdForLevel
      ? this.getCurrentService().getThresholdForLevel(level)
      : 0
  }

  /**
   * Create reactive refs for progression values derived from a reactive character ref.
   * This allows UI to consume progression info without importing xpStore directly.
   * @param {import('vue').Ref|Object} maybeRef - a ref to the character object or a plain character
   * @returns {{ progress: Ref<number>, nextLevelXP: Ref<number>, threshold: Ref<number>, details: Ref<object> }}
   */
  createProgressionRefs(maybeRef) {
    // Accept either a Vue ref or a plain object
    const charRef =
      maybeRef && typeof maybeRef === 'object' && 'value' in maybeRef
        ? maybeRef
        : { value: maybeRef }

    const progress = computed(() => this.getExperienceProgress(charRef.value))
    const nextLevelXP = computed(() => this.getNextLevelXP(charRef.value))
    const threshold = computed(() =>
      this.getThresholdForLevel(this.getTotalLevel(charRef.value) + 1)
    )
    const details = computed(() => this.getExperienceProgressDetails(charRef.value))

    return { progress, nextLevelXP, threshold, details }
  }

  // Set progression speed centrally (accepts speed string or character object)
  setProgressionSpeed(characterOrSpeed) {
    return this.getCurrentService().setProgressionSpeed(characterOrSpeed)
  }

  validateCharacter(character) {
    return this.getCurrentService().validateCharacter(character)
  }

  getCharacterSummary(character) {
    return this.getCurrentService().getCharacterSummary(character)
  }

  getTotalLevel(character) {
    return this.getCurrentService().getTotalLevel(character)
  }

  getPrimaryClass(character) {
    return this.getCurrentService().getPrimaryClass(character)
  }

  isMulticlassed(character) {
    return this.getCurrentService().isMulticlassed(character)
  }

  /**
   * Validate and clean entity indexes against authoritative stores
   * @param {Object} characterData Character data to validate
   * @param {string} gameVersionId Game version ID (e.g., 'dnd_5e') - uses stores folder structure
   * @returns {Promise<Object>} { cleanedData, report }
   */
  async validateAndCleanIndexes(characterData, gameVersionId = 'dnd_5e') {
    // Pass version ID directly to service (stores use version ID, not code)
    return this.getCurrentService().validateAndCleanIndexes(characterData, gameVersionId)
  }

  // Delegator: build minimal AI image context per version
  getAiImageContext(character) {
    const svc = this.getCurrentService()
    if (typeof svc.buildAiImageContext === 'function') return svc.buildAiImageContext(character)
    // Fallback: very small subset if version service doesn't implement
    const c = character || {}
    return {
      name: c.name || undefined,
      race: c.race || undefined,
      classes: Array.isArray(c.classes)
        ? c.classes.map((cl) => ({ name: cl?.name, level: cl?.level }))
        : [],
      backstory: c.backstory || c.description || undefined,
      avatarUrl: c.avatar || undefined
    }
  }

  /**
   * Delegator: build a compact AI text context for textual tasks
   * Version services may implement buildAiTextContext(character) for richer context
   */
  getAiTextContext(character) {
    const svc = this.getCurrentService()
    if (typeof svc.buildAiTextContext === 'function') return svc.buildAiTextContext(character)
    // Fallback: produce a small object with truncated backstory and key fields
    const c = character || {}
    const backstory = c.backstory || c.description || ''
    return {
      name: c.name || undefined,
      race: c.race || undefined,
      classes: Array.isArray(c.classes)
        ? c.classes.map((cl) => ({ name: cl?.name, level: cl?.level }))
        : [],
      appearance: c.appearance || undefined,
      backstory: typeof backstory === 'string' ? backstory.slice(0, 1200) : undefined,
      personalityTraits: c.personalityTraits || undefined,
      avatarUrl: c.avatar || undefined
    }
  }
}

export default new CharacterService()
